import toast from 'react-hot-toast';
import { getContentTypeForExtension } from '@/constants/supported-file-types';
import { SUPPORTED_FILE_EXTENSIONS } from './fileFilterUtils';

/**
 * Extracts files from a zip archive and returns them as File objects
 * Preserves folder structure using webkitRelativePath
 */
export async function extractZipFile(zipFile: File): Promise<File[]> {
  try {
    // Lazy-import JSZip so it isn't pulled into the initial bundle — it's only
    // needed when a user actually extracts a zip during upload.
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(zipFile);
    const extractedFiles: File[] = [];

    // Process all files in the zip
    for (const [path, zipEntry] of Object.entries(zip.files)) {
      const normalizedPath = path.replace(/\\/g, '/');

      // Skip directories and macOS metadata
      if (zipEntry.dir || normalizedPath.includes('__MACOSX') || normalizedPath.startsWith('.')) {
        continue;
      }

      // Check if file has supported extension
      const fileName = normalizedPath.split('/').pop() || '';
      const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();

      if (!SUPPORTED_FILE_EXTENSIONS.includes(extension) || extension === '.zip') {
        continue; // Skip unsupported files and nested zips
      }

      try {
        // Extract file content as blob
        const blob = await zipEntry.async('blob');

        // Create File object with proper metadata
        const file = new File([blob], fileName, {
          type: blob.type || getContentTypeForExtension(extension),
          lastModified: zipEntry.date?.getTime() || Date.now(),
        });

        // Set webkitRelativePath to preserve internal folder structure from the zip
        // Strip apostrophes — they cause Azure SAS signature mismatches
        const relativePath = normalizedPath.replace(/'/g, '');
        Object.defineProperty(file, 'webkitRelativePath', {
          value: relativePath,
          writable: false,
          configurable: true,
        });

        extractedFiles.push(file);
      } catch (error) {
        console.error(`Failed to extract ${path}:`, error);
      }
    }

    return extractedFiles;
  } catch (_error) {
    toast.error('Failed to extract zip file. It may be corrupted.');
    return [];
  }
}

/**
 * Checks if a file is a zip file
 */
export function isZipFile(file: File): boolean {
  return file.name.toLowerCase().endsWith('.zip');
}
